import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, X, GitCompare, ExternalLink } from "lucide-react";
import { fetchPrinter } from "@/lib/api";
import StarRating from "@/components/StarRating";
import LoadingState from "@/components/LoadingState";
import { TYPE_LABELS } from "@/lib/constants";

export default function ReviewDetail() {
    const { slug } = useParams();
    const [printer, setPrinter] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        fetchPrinter(slug)
            .then(setPrinter)
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) return <LoadingState label="Loading review" />;
    if (notFound || !printer) return (
        <div className="container-tight py-24 text-center" data-testid="not-found">
            <h1 className="font-display text-4xl text-white">Review not found</h1>
            <Link to="/reviews" className="btn-primary mt-6">Back to reviews</Link>
        </div>
    );

    return (
        <div data-testid="review-detail-page">
            {/* Breadcrumb */}
            <div className="container-tight pt-8">
                <Link to="/reviews" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="back-to-reviews">
                    <ArrowLeft className="h-4 w-4" /> All reviews
                </Link>
            </div>

            {/* Header */}
            <section className="container-tight pt-8 pb-12 border-b border-zinc-800">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <div className="flex flex-wrap gap-2 mb-6">
                            <span className="bg-zinc-950 border border-zinc-800 text-[10px] tracking-[0.22em] uppercase text-zinc-300 px-3 py-1.5">{printer.brand}</span>
                            <span className="bg-blue-600 text-[10px] tracking-[0.22em] uppercase text-white px-3 py-1.5">{TYPE_LABELS[printer.type] || printer.type}</span>
                            <span className="border border-zinc-800 text-[10px] tracking-[0.22em] uppercase text-zinc-400 px-3 py-1.5">Reviewed {printer.year}</span>
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.02]" data-testid="printer-title">
                            {printer.name}
                        </h1>
                        <p className="text-lg md:text-xl text-zinc-400 mt-5 leading-relaxed max-w-2xl">
                            {printer.tagline}
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-6">
                            <div>
                                <StarRating value={printer.rating} size="lg" />
                                <div className="text-xs text-zinc-500 mt-1 tracking-[0.2em] uppercase">Overall Rating</div>
                            </div>
                            <div className="h-10 border-l border-zinc-800" />
                            <div>
                                <div className="font-mono text-2xl text-white">${printer.price_usd.toFixed(2)}</div>
                                <div className="text-xs text-zinc-500 mt-1 tracking-[0.2em] uppercase">MSRP</div>
                            </div>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to={`/compare?ids=${printer.id}`} className="btn-primary" data-testid="add-to-compare-cta">
                                <GitCompare className="h-4 w-4" /> Add to comparison
                            </Link>
                            <a href="#full-review" className="btn-ghost">
                                Jump to full review
                            </a>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="card-flat overflow-hidden">
                            <div className="aspect-[5/4] bg-zinc-900">
                                <img src={printer.image} alt={printer.name} className="w-full h-full object-cover" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Body: full review + specs bento */}
            <section className="container-tight py-16 md:py-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <article className="lg:col-span-8 space-y-10" id="full-review">
                        <div>
                            <div className="eyebrow">Summary</div>
                            <p className="text-lg text-zinc-300 leading-relaxed mt-4">{printer.summary}</p>
                        </div>

                        <div className="border-t border-zinc-800 pt-10">
                            <div className="eyebrow">Full review</div>
                            <div className="mt-4 prose prose-invert max-w-none">
                                {printer.review.split("\n\n").map((para, i) => (
                                    <p key={i} className="text-zinc-300 leading-relaxed text-base md:text-lg mb-5">{para}</p>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
                            <div className="bg-zinc-950 p-6 md:p-8">
                                <div className="flex items-center gap-2 text-emerald-400">
                                    <Check className="h-4 w-4" />
                                    <div className="eyebrow !text-emerald-400">The Good</div>
                                </div>
                                <ul className="mt-5 space-y-3" data-testid="pros-list">
                                    {printer.pros.map((p, i) => (
                                        <li key={i} className="flex gap-3 text-sm text-zinc-300 leading-relaxed">
                                            <span className="text-emerald-500 mt-1">+</span>
                                            <span>{p}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="bg-zinc-950 p-6 md:p-8">
                                <div className="flex items-center gap-2 text-red-400">
                                    <X className="h-4 w-4" />
                                    <div className="eyebrow !text-red-400">The Not-So-Good</div>
                                </div>
                                <ul className="mt-5 space-y-3" data-testid="cons-list">
                                    {printer.cons.map((c, i) => (
                                        <li key={i} className="flex gap-3 text-sm text-zinc-300 leading-relaxed">
                                            <span className="text-red-500 mt-1">–</span>
                                            <span>{c}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </article>

                    <aside className="lg:col-span-4">
                        <div className="lg:sticky lg:top-24 space-y-4">
                            <div className="card-flat" data-testid="specs-panel">
                                <div className="p-5 border-b border-zinc-800">
                                    <div className="eyebrow">Full Specs</div>
                                </div>
                                <dl>
                                    {Object.entries(printer.specs).map(([k, v], i) => (
                                        <div key={k} className={`flex justify-between gap-4 px-5 py-3.5 ${i > 0 ? "border-t border-zinc-900" : ""}`}>
                                            <dt className="text-xs tracking-wide text-zinc-500 uppercase">{k}</dt>
                                            <dd className="text-sm text-zinc-100 text-right font-medium">{v}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                            <div className="text-[11px] text-zinc-500 leading-relaxed border border-zinc-800 rounded-sm p-4" data-testid="review-disclaimer">
                                <strong className="text-zinc-300">Editorial notice:</strong> This review is provided for informational
                                and educational purposes. {printer.brand} and the {printer.name} name are trademarks of their respective owners.
                                All About Printers is not endorsed by or affiliated with {printer.brand}. Always verify current specs,
                                pricing, and availability with the manufacturer before purchasing.
                            </div>
                        </div>
                    </aside>
                </div>
            </section>
        </div>
    );
}
